import React from "react";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";

/**
 * Reusable KPI tile: title, big value, optional sub, optional delta chip,
 * optional accent color (hsl string), optional onClick (cross-filter / navigate).
 */
export function KpiTile({ title, value, sub, accent = "hsl(var(--primary))", icon: Icon, delta, deltaTone, onClick, children, testId, className }) {
  const clickable = typeof onClick === "function";
  const toneCls = deltaTone === "down"
    ? "bg-[hsl(var(--danger)/0.12)] text-[hsl(var(--danger))]"
    : deltaTone === "flat"
      ? "bg-[hsl(var(--muted))] text-muted-foreground"
      : "bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))]";
  return (
    <Card
      data-testid={testId}
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter") onClick(); } : undefined}
      className={cn(
        "relative overflow-hidden rounded-xl border bg-card p-4 sm:p-5 card-shadow card-shadow-hover",
        clickable && "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(var(--background))]",
        className
      )}
    >
      <span className="absolute left-0 top-0 h-full w-1" style={{ background: accent }} />
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs font-medium text-muted-foreground">{title}</p>
        {Icon && (
          <span className="flex h-7 w-7 items-center justify-center rounded-lg" style={{ background: `${accent}1f`, color: accent }}>
            <Icon className="h-4 w-4" />
          </span>
        )}
      </div>
      <div className="mt-2 flex items-end justify-between gap-2">
        <p className="font-heading text-2xl sm:text-[1.7rem] font-semibold tracking-tight tabular-nums" style={{ color: accent }}>
          {value}
        </p>
        {delta != null && (
          <span className={cn("inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-medium", toneCls)}>
            {deltaTone === "down" ? <ArrowDownRight className="h-3 w-3" /> : deltaTone === "flat" ? null : <ArrowUpRight className="h-3 w-3" />}
            {delta}
          </span>
        )}
      </div>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
      {children && <div className="mt-3">{children}</div>}
    </Card>
  );
}
